import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material'

export default function TwoFactorSetup({
  qr_code_svg: qrCodeSvg,
  provisioning_uri: provisioningUri,
  secret,
  backup_codes: backupCodes,
  flash = {},
}) {
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [codesSaved, setCodesSaved] = useState(false)

  const handleVerify = (e) => {
    e.preventDefault()
    setSubmitting(true)
    router.post(
      '/two_factor/setup',
      { code },
      { onFinish: () => setSubmitting(false) }
    )
  }

  const handleConfirm = (e) => {
    e.preventDefault()
    setSubmitting(true)
    router.post('/two_factor/setup/confirm', {}, { onFinish: () => setSubmitting(false) })
  }

  const handleDownloadCodes = () => {
    if (!backupCodes) return
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'okonomi-backup-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
          Zwei-Faktor-Authentifizierung einrichten
        </Typography>

        {flash.alert && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }} data-testid="tfa-setup-alert">
            {flash.alert}
          </Alert>
        )}
        {flash.notice && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }} data-testid="tfa-setup-notice">
            {flash.notice}
          </Alert>
        )}

        {!backupCodes && (
          <Paper elevation={2} sx={{ p: 4, width: '100%' }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Scannen Sie diesen QR-Code mit einer Authenticator-App
              (z. B. Google Authenticator oder Authy).
            </Typography>
            {qrCodeSvg && (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
                data-testid="tfa-qr-container"
                dangerouslySetInnerHTML={{ __html: qrCodeSvg }}
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Manuelles Setup (geheimer Schlüssel):
            </Typography>
            <Box
              sx={{
                p: 1,
                mb: 2,
                bgcolor: 'background.subtle',
                fontFamily: 'monospace',
                fontSize: 14,
                wordBreak: 'break-all',
              }}
              data-testid="tfa-secret"
            >
              {secret}
            </Box>

            <Box component="form" onSubmit={handleVerify} noValidate>
              <TextField
                label="Code aus der App"
                fullWidth
                margin="normal"
                autoFocus
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'tfa-setup-code-input', inputMode: 'numeric' } }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={submitting || code.trim().length === 0}
                sx={{ mt: 2 }}
                data-testid="tfa-setup-submit-button"
              >
                {submitting ? 'Prüfen...' : 'Code bestätigen'}
              </Button>
            </Box>
          </Paper>
        )}

        {backupCodes && (
          <Paper elevation={2} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Backup-Codes
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Speichern Sie diese Codes sicher ab. Jeder Code funktioniert nur
              einmal und ersetzt Ihre Authenticator-App, falls Sie keinen
              Zugriff darauf haben.
            </Typography>
            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'background.subtle',
                fontFamily: 'monospace',
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
              }}
              data-testid="tfa-backup-codes"
            >
              {backupCodes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </Box>
            <Button
              variant="outlined"
              onClick={handleDownloadCodes}
              sx={{ mb: 2 }}
              data-testid="tfa-download-codes-button"
            >
              Codes herunterladen
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={codesSaved}
                  onChange={(e) => setCodesSaved(e.target.checked)}
                  slotProps={{ input: { 'data-testid': 'tfa-codes-saved-checkbox' } }}
                />
              }
              label="Ich habe die Codes sicher gespeichert."
              sx={{ display: 'block', mb: 2 }}
            />
            <Button
              type="button"
              fullWidth
              variant="contained"
              size="large"
              disabled={!codesSaved || submitting}
              onClick={handleConfirm}
              data-testid="tfa-setup-confirm-button"
            >
              Weiter zur Anwendung
            </Button>
          </Paper>
        )}
      </Box>
    </Container>
  )
}
